import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export interface WatchlistItem {
  id: number;
  userId: number;
  productId: number;
  product: {
    id: number;
    label?: string;
    name?: string;
    priceHT?: number | string;
    price?: number | string;
    discount_price?: number | string;
    image?: string;
    imageUrl?: string;
    images?: string[];
    availability?: number | string | null; // 0: In Stock, 1: Available on backorder, 2: Arriving Soon, 3: Made to Order
    stock?: number;
    brand?: string;
    description?: string;
    reference?: string;
    discountRate?: number | string; // Discount percentage
    tvaRate?: number | string; // VAT rate
    unit_sales?: {
      code: string;
      label: string;
      minimal_quantity?: number | string;
    };
    unit_product?: {
      code: string;
      label: string;
      minimal_quantity?: number | string;
    };
    variants?: Array<{
      value?: string;
      show?: boolean;
      variant?: {
        label?: string;
      };
    }>;
    documents?: Array<{
      id: number;
      label: string;
      url: string;
    }>;
    category?: {
      id: number;
      label: string;
    };
    colors?: Array<{
      id: number;
      colorCode: string;
      label: string;
      documents?: Array<{
        id: number;
        label: string;
        url: string;
      }>;
    }>;
  };
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class WatchlistService {
  private baseUrl = `${environment.baseUrl}/watchlist`;
  private watchlistSubject = new BehaviorSubject<WatchlistItem[]>([]);
  private watchlistRequest: Observable<WatchlistItem[]> | null = null;
  private watchlistLoaded = false;
  private watchlistOwnerId: number | null = null;
  private watchlistRequestVersion = 0;
  private readonly mutations = new Map<
    number,
    { operation: 'add' | 'remove'; request: Observable<unknown> }
  >();
  public watchlist$ = this.watchlistSubject.asObservable();

  constructor(private http: HttpClient, private authService: AuthService) {}

  getWatchlist(): Observable<WatchlistItem[]> {
    if (!this.authService.isLoggedIn()) {
      if (this.watchlistOwnerId !== null || this.watchlistLoaded || this.watchlistRequest) {
        this.resetWatchlistCache(null);
      }
      return of([]);
    }

    const ownerId = this.authService.getCurrentUser()?.id ?? null;
    if (ownerId !== this.watchlistOwnerId) {
      this.resetWatchlistCache(ownerId);
    }
    if (this.watchlistLoaded) {
      return of(this.watchlistSubject.value);
    }
    if (this.watchlistRequest) {
      return this.watchlistRequest;
    }

    const requestVersion = this.watchlistRequestVersion;
    let request: Observable<WatchlistItem[]>;
    request = this.http.get<WatchlistItem[]>(this.baseUrl).pipe(
      map(items => this.applyPendingMutations(items)),
      tap(items => {
        if (requestVersion === this.watchlistRequestVersion && ownerId === this.watchlistOwnerId) {
          this.watchlistSubject.next(items);
          this.watchlistLoaded = true;
        }
      }),
      catchError(this.handleError),
      finalize(() => {
        if (this.watchlistRequest === request) {
          this.watchlistRequest = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.watchlistRequest = request;
    return request;
  }

  reloadWatchlist(): Observable<WatchlistItem[]> {
    if (!this.authService.isLoggedIn()) {
      this.resetWatchlistCache(null);
      return of([]);
    }

    const ownerId = this.authService.getCurrentUser()?.id ?? null;
    if (ownerId !== this.watchlistOwnerId) {
      this.resetWatchlistCache(ownerId);
    } else {
      this.invalidateWatchlistRequest();
    }
    return this.getWatchlist();
  }

  addToWatchlist(productId: number): Observable<unknown> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Please log in to add products to your watchlist'));
    }
    return this.mutateWatchlist(
      productId,
      'add',
      () => this.http.post(this.baseUrl, { productId })
    );
  }

  removeFromWatchlist(productId: number): Observable<unknown> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Please log in to manage your watchlist'));
    }
    return this.mutateWatchlist(
      productId,
      'remove',
      () => this.http.delete(`${this.baseUrl}/${productId}`)
    );
  }

  isMutationPending(productId: number): boolean {
    return this.mutations.has(productId);
  }

  containsProduct(productId: number): boolean {
    return this.watchlistSubject.value.some(
      item => Number(item.productId ?? item.product?.id) === Number(productId)
    );
  }

  isInWatchlist(productId: number): Observable<boolean> {
    if (!this.authService.isLoggedIn()) {
      return of(false);
    }
    
    if (this.watchlistLoaded) {
      return of(this.containsProduct(productId));
    }
    
    // If no items loaded yet, fetch from server and return boolean result
    return new Observable<boolean>(subscriber => {
      this.getWatchlist().subscribe({
        next: (items) => {
          subscriber.next(
            items.some(
              item => Number(item.productId ?? item.product?.id) === Number(productId)
            )
          );
          subscriber.complete();
        },
        error: () => {
          subscriber.next(false);
          subscriber.complete();
        }
      });
    });
  }

  refreshWatchlist(): void {
    if (this.authService.isLoggedIn()) {
      const ownerId = this.authService.getCurrentUser()?.id ?? null;
      if (ownerId !== this.watchlistOwnerId) {
        this.resetWatchlistCache(ownerId);
      } else {
        this.invalidateWatchlistRequest();
      }
      this.getWatchlist().subscribe({
        error: error => console.error('Failed to refresh the watchlist', error)
      });
    }
  }

  private mutateWatchlist(
    productId: number,
    operation: 'add' | 'remove',
    requestFactory: () => Observable<unknown>
  ): Observable<unknown> {
    if (!Number.isInteger(productId) || productId <= 0) {
      return throwError(() => new Error('A valid product is required to update your watchlist'));
    }

    const existingMutation = this.mutations.get(productId);
    if (existingMutation) {
      return existingMutation.operation === operation
        ? existingMutation.request
        : throwError(() => new Error('A watchlist update is already in progress for this product'));
    }

    let request: Observable<unknown>;
    const previousItem = this.watchlistSubject.value.find(
      item => Number(item.productId ?? item.product?.id) === productId
    );
    this.applyOptimisticChange(productId, operation, previousItem);

    let mutationFailed = false;
    request = requestFactory().pipe(
      tap(() => this.refreshWatchlist()),
      catchError(error => {
        mutationFailed = true;
        this.rollbackOptimisticChange(productId, operation, previousItem);
        return this.handleError(error);
      }),
      finalize(() => {
        if (this.mutations.get(productId)?.request === request) {
          this.mutations.delete(productId);
        }
        if (mutationFailed) {
          this.refreshWatchlist();
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.mutations.set(productId, { operation, request });
    return request;
  }

  private applyOptimisticChange(
    productId: number,
    operation: 'add' | 'remove',
    existingItem?: WatchlistItem
  ): void {
    this.invalidateWatchlistRequest();
    const items = this.watchlistSubject.value;
    if (operation === 'add' && !existingItem) {
      this.watchlistSubject.next([
        ...items,
        {
          id: 0,
          userId: this.authService.getCurrentUser()?.id ?? 0,
          productId,
          product: { id: productId },
          createdAt: new Date().toISOString()
        }
      ]);
    } else if (operation === 'remove' && existingItem) {
      this.watchlistSubject.next(items.filter(
        item => Number(item.productId ?? item.product?.id) !== productId
      ));
    }
  }

  private rollbackOptimisticChange(
    productId: number,
    operation: 'add' | 'remove',
    previousItem?: WatchlistItem
  ): void {
    const items = this.watchlistSubject.value;
    const productExists = items.some(
      item => Number(item.productId ?? item.product?.id) === productId
    );

    if (operation === 'add' && !previousItem) {
      this.watchlistSubject.next(items.filter(
        item => Number(item.productId ?? item.product?.id) !== productId
      ));
    } else if (operation === 'remove' && previousItem && !productExists) {
      this.watchlistSubject.next([...items, previousItem]);
    }
    this.invalidateWatchlistRequest();
  }

  private applyPendingMutations(items: WatchlistItem[]): WatchlistItem[] {
    return Array.from(this.mutations.entries()).reduce((currentItems, [productId, mutation]) => {
      const hasProduct = currentItems.some(
        item => Number(item.productId ?? item.product?.id) === productId
      );

      if (mutation.operation === 'remove') {
        return currentItems.filter(
          item => Number(item.productId ?? item.product?.id) !== productId
        );
      }

      if (hasProduct) return currentItems;
      return [
        ...currentItems,
        {
          id: 0,
          userId: this.authService.getCurrentUser()?.id ?? 0,
          productId,
          product: { id: productId },
          createdAt: new Date().toISOString()
        }
      ];
    }, items);
  }

  private invalidateWatchlistRequest(): void {
    this.watchlistLoaded = false;
    this.watchlistRequest = null;
    this.watchlistRequestVersion++;
  }

  private resetWatchlistCache(ownerId: number | null): void {
    this.watchlistOwnerId = ownerId;
    this.watchlistLoaded = false;
    this.watchlistRequest = null;
    this.watchlistRequestVersion++;
    this.watchlistSubject.next([]);
  }

  private handleError = (error: HttpErrorResponse) => {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 401:
          errorMessage = 'Please log in to access your watchlist';
          break;
        case 404:
          errorMessage = 'Product not found';
          break;
        case 500:
          errorMessage = 'Server error occurred';
          break;
        default:
          errorMessage = `Error ${error.status}: ${error.message}`;
      }
    }
    
    return throwError(() => new Error(errorMessage));
  };
}
